import {
  ARTIFACTS_PATH,
  APP_RESOURCE,
  DEFAULT_CUSTOM_LISTING_LANGUAGE,
  INVENTORY_SCAN_RESOURCE,
  LISTING_SNAPSHOT_RESOURCE,
  MARKET_LANGUAGES,
  STORE_LISTING_RESOURCE,
  type AppResource,
  type InventoryApp,
  type InventoryCell,
  type InventoryListing,
  type TrackerImportReport,
} from '@studio/shared';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArchivedApps } from '../app/Archive.js';
import { api, ApiError } from '../shell/api.js';
import { EmptyState, Failure } from '../shell/EmptyState.js';
import { Link, type Surface } from '../shell/router.js';
import { useResourceEvents } from '../shell/stream.js';
import { useAsync } from '../shell/useAsync.js';
import {
  ASSET_LABEL,
  CELL_LABEL,
  FACET_LABEL,
  FACETS,
  GRAPHICS_LABEL,
  STANDING_LABEL,
  cellAddress,
  cellGraphicsOf,
  cellInAddress,
  comparisonAddress,
  comparisonInAddress,
  counted,
  countryName,
  coverageOf,
  facetCounts,
  gapsByLanguage,
  inFacet,
  languageName,
  lineOf,
  listingScanOf,
  readingWhen,
  scanLine,
  type CellGraphics,
  type ComparisonTarget,
  type CountryLine,
  type Facet,
  type ListingLine,
  type OpenCell,
} from './model.js';
import { readById, StartExperiment, type ReadingById } from './experiment.js';
import { Comparison } from './comparison.js';
import { csvFile, downloadCsv } from './csv.js';

/** A reading the operator opened, and where: it opens under what was pressed. */
interface Showing {
  readonly cell: InventoryCell;
  readonly from: 'gaps' | 'countries';
}

function sameCell(left: InventoryCell | null, right: InventoryCell | undefined): boolean {
  return left !== null && right !== undefined && left.market === right.market && left.locale === right.locale;
}

interface Filters {
  readonly query: string;
  readonly facet: Facet;
}

const NO_FILTERS: Filters = { query: '', facet: 'all' };

/**
 * The filters live in the address: a reading is corrected on the App page, and coming
 * back must land on the same narrowed list, not on the whole Inventory again.
 */
function filtersInAddress(): Filters {
  const params = new URLSearchParams(window.location.search);
  const show = params.get('show');
  return { query: params.get('q') ?? '', facet: FACETS.find((facet) => facet === show) ?? 'all' };
}

function keepInAddress(filters: Filters): void {
  const params = new URLSearchParams();
  if (filters.query !== '') params.set('q', filters.query);
  if (filters.facet !== 'all') params.set('show', filters.facet);
  // The open cell is in the address too: a filter must not close the reading.
  const cell = cellInAddress(window.location.search);
  if (cell !== null) params.set('cell', cellAddress(cell));
  // And so is the open comparison: a filter must not close that either.
  const comparison = comparisonInAddress(window.location.search);
  if (comparison !== null) params.set('compare', comparisonAddress(comparison));
  writeInAddress(params);
}

/** The open cell goes into the address: a return must find the same reading open. */
function rememberCellInAddress(cell: OpenCell | null): void {
  const params = new URLSearchParams(window.location.search);
  if (cell === null) params.delete('cell');
  else params.set('cell', cellAddress(cell));
  writeInAddress(params);
}

/** The open comparison goes into the address: a return must find the same comparison open. */
function rememberComparisonInAddress(comparison: ComparisonTarget | null): void {
  const params = new URLSearchParams(window.location.search);
  if (comparison === null) params.delete('compare');
  else params.set('compare', comparisonAddress(comparison));
  writeInAddress(params);
}

function writeInAddress(params: URLSearchParams): void {
  const search = params.toString();
  const address = `${window.location.pathname}${search === '' ? '' : `?${search}`}`;
  if (address === `${window.location.pathname}${window.location.search}`) return;
  try {
    window.history.replaceState(window.history.state, '', address);
  } catch {
    // A browser that rations address changes keeps the filters on the page all the same.
  }
}

/** The App's scan is going: the Store Listings it has queued are being read. */
function appIsReading(entry: InventoryApp): boolean {
  return entry.latestScan?.state === 'reading';
}

/** An App as the Inventory lists it. */
interface AppGroup {
  readonly entry: InventoryApp;
  /** The Store Listings shown: every one of them until a search or a facet narrows the App. */
  readonly lines: readonly ListingLine[];
  /** Counted over every Store Listing of the App, shown or not. */
  readonly withGaps: number;
}

function groupOf(entry: InventoryApp): AppGroup {
  const lines = entry.listings.map(lineOf);
  return {
    entry,
    lines,
    withGaps: lines.filter((line) => inFacet('gaps', line)).length,
  };
}

/** The App with the Store Listings a search finds, or nothing at all. */
function searched(group: AppGroup, text: string): AppGroup[] {
  const query = text.trim().toLowerCase();
  if (query === '') return [group];
  const { app } = group.entry;
  if (app.displayName.toLowerCase().includes(query) || (app.packageName ?? '').toLowerCase().includes(query)) return [group];
  const lines = group.lines.filter(
    ({ listing }) =>
      listing.storeListing.name.toLowerCase().includes(query) || (listing.storeListing.listingParam ?? '').toLowerCase().includes(query),
  );
  return lines.length === 0 ? [] : [{ ...group, lines }];
}

/** The App with the Store Listings of one facet, or nothing at all. */
function narrowed(group: AppGroup, facet: Facet): AppGroup[] {
  if (facet === 'all') return [group];
  const lines = group.lines.filter((line) => inFacet(facet, line));
  return lines.length === 0 ? [] : [{ ...group, lines }];
}

const listed = (groups: readonly AppGroup[]): number => groups.reduce((sum, group) => sum + group.lines.length, 0);

/** The languages with the most countries lead; the rest are one press away. */
const GAP_LANGUAGES_SHOWN = 12;

const WATCHED = new Set<string>([INVENTORY_SCAN_RESOURCE, STORE_LISTING_RESOURCE, LISTING_SNAPSHOT_RESOURCE, APP_RESOURCE]);

/**
 * The last App that went into the archive or came back out of it. An App leaves the
 * ledger the moment it is archived, so what happened to it - and the way to take it
 * back - is said where the operator is looking, not at the foot of the page.
 */
interface ArchiveNotice {
  readonly app: AppResource;
  readonly archived: boolean;
  readonly failure: string | null;
}

/** What the last press of «Получить из Tracker» came to. */
type TrackerImportOutcome =
  | { readonly report: TrackerImportReport }
  /** `onProviders`: the Tracker's address or token is what has to be put right. */
  | { readonly failure: string; readonly onProviders: boolean };

/**
 * The inventory: every App, every Store Listing it has, and what each one shows in
 * every Market Locale. Read from the public storefront, never from Play Console.
 *
 * It is a ledger: a Store Listing is one line, the facets say how many of them share
 * what there is to act on, and everything else about a listing opens under its line.
 */
function Inventory(): ReactNode {
  const loaded = useAsync(async () => api.readInventory(), []);
  const { reload } = loaded;
  const [filters, setFilters] = useState<Filters>(filtersInAddress);
  useEffect(() => keepInAddress(filters), [filters]);
  // The Apps the operator folded away. A new filter opens them all again: what it found
  // is the point of it.
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const filter = (next: Filters): void => {
    setFilters(next);
    setFolded(new Set());
  };
  const [scanning, setScanning] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // A scan announces itself every few pages; one re-read covers a burst of them.
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (pending.current !== null) clearTimeout(pending.current);
  }, []);
  useResourceEvents((event) => {
    if (!WATCHED.has(event.resource) || pending.current !== null) return;
    pending.current = setTimeout(() => {
      pending.current = null;
      reload();
    }, 1_500);
  });

  const scanAll = (): void => {
    setScanning(true);
    setFailure(null);
    void api
      .scanAll()
      .then(reload)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось запустить сканирования.'))
      .finally(() => setScanning(false));
  };

  // One Tracker Import at a time: the button is off until the Studio has answered.
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<TrackerImportOutcome | null>(null);
  const importFromTracker = (): void => {
    setImporting(true);
    setImported(null);
    void api
      .importFromTracker()
      .then((report) => {
        setImported({ report });
        reload();
      })
      .catch((caught: unknown) =>
        setImported(
          caught instanceof ApiError
            ? { failure: caught.message, onProviders: caught.status === 400 }
            : { failure: 'Не удалось получить данные из Tracker.', onProviders: false },
        ),
      )
      .finally(() => setImporting(false));
  };
  const [notice, setNotice] = useState<ArchiveNotice | null>(null);
  const archived = (app: AppResource): void => {
    setNotice({ app, archived: true, failure: null });
    reload();
  };
  const restored = (app: AppResource): void => {
    setNotice({ app, archived: false, failure: null });
    reload();
  };
  const restore = (app: AppResource): void => {
    void api
      .restoreApp(app.id)
      .then(restored)
      .catch((caught: unknown) =>
        setNotice({ app, archived: true, failure: caught instanceof ApiError ? caught.message : 'Не удалось вернуть App из архива.' }),
      );
  };

  const importButton = (className: string): ReactNode => (
    <button type="button" className={className} disabled={importing} onClick={importFromTracker}>
      {importing ? 'Получаю из Tracker…' : 'Получить из Tracker'}
    </button>
  );

  // Judged once per reading of the Inventory, not once per keystroke in the search.
  const groups = useMemo(() => (loaded.value?.apps ?? []).map(groupOf), [loaded.value]);
  const found = useMemo(() => groups.flatMap((group) => searched(group, filters.query)), [groups, filters.query]);
  // The facets count what the search found, so a count is what pressing it will show.
  const facetTotals = useMemo(() => facetCounts(found.flatMap((group) => group.lines)), [found]);
  const shown = useMemo(() => found.flatMap((group) => narrowed(group, filters.facet)), [found, filters.facet]);

  /**
   * The visible set, as a file.
   *
   * The search and the filter live only in the browser, so the export is built from the
   * rows the page is already showing. What the operator sees is what the file holds -
   * there is no second notion of "visible" to disagree with it.
   */
  const exportCsv = (): void => {
    const file = csvFile(
      shown.flatMap((group) =>
        group.lines.map((line) => ({ app: group.entry.app, appReading: appIsReading(group.entry), listing: line.listing })),
      ),
      { query: filters.query, facet: filters.facet },
    );
    downloadCsv(file.name, file.text);
  };

  if (loaded.error !== null) {
    return (
      <section className="page">
        <p className="eyebrow">Inventory</p>
        <Failure message={loaded.error} onRetry={reload} />
      </section>
    );
  }
  if (loaded.value === null) {
    return (
      <section className="page" aria-busy="true">
        <p className="eyebrow">Inventory · публичная витрина</p>
        <p className="note">Читаю Inventory…</p>
      </section>
    );
  }

  const allFolded = shown.length > 0 && shown.every((group) => folded.has(group.entry.app.id));
  const fold = (appId: string): void =>
    setFolded((current) => {
      const next = new Set(current);
      if (!next.delete(appId)) next.add(appId);
      return next;
    });
  const everyListing = listed(groups);

  return (
    <section className="page inventory">
      <div className="page-head">
        <div>
          <p className="eyebrow">Inventory · публичная витрина</p>
          <h1>Каждый Store Listing и что он показывает где.</h1>
          <p className="note">Читается из Google Play так, как видят пользователи, страна за страной, не из Play Console.</p>
        </div>
        <div className="inventory-actions">
          <div className="row-actions">
            {importButton('secondary')}
            <button type="button" className="secondary" onClick={exportCsv}>
              Выгрузить CSV
            </button>
            <button type="button" className="primary" disabled={scanning || groups.length === 0} onClick={scanAll}>
              {scanning ? 'Запускаю…' : 'Scan all'}
            </button>
          </div>
          <p className="note">
            Scan all читает по одному App, сначала самые старые чтения. Из Tracker приходят Apps и Custom Store Listings, которых
            здесь нет. CSV выгружает то, что видно при текущем поиске и фильтре.
          </p>
          {/* An export of nothing is said where the export is, and not only in the empty screen below. */}
          {shown.length === 0 ? (
            <p className="note">Выгружать нечего: под текущий поиск и фильтр не подошёл ни один Store Listing.</p>
          ) : null}
        </div>
      </div>
      {failure === null ? null : <Failure message={failure} />}
      {imported === null ? null : 'report' in imported ? (
        <TrackerImportSummary report={imported.report} />
      ) : (
        <div className="failure" role="alert">
          <p>
            {imported.failure}
            {imported.onProviders ? (
              <>
                {' '}
                <Link to="/settings/providers">Открыть Providers</Link>
              </>
            ) : null}
          </p>
        </div>
      )}

      {groups.length === 0 ? (
        <EmptyState
          title={loaded.value.archived.length === 0 ? 'На Google Play ещё нет App.' : 'Все App в архиве.'}
          note={
            loaded.value.archived.length === 0
              ? 'Добавьте App по адресу Play или имени пакета в Workspace — и его Main Store Listing сразу сканируется. Или получите Apps и их Custom Store Listings из Tracker.'
              : 'Верните нужный App из архива ниже. Или добавьте новый по адресу Play или имени пакета в Workspace, или получите Apps из Tracker.'
          }
          action={importButton('primary')}
        />
      ) : (
        <>
          <div className="inventory-toolbar" role="group" aria-label="Фильтр Inventory">
            <input
              type="search"
              className="inventory-search"
              aria-label="Найти App, пакет или listing"
              placeholder="Найти App, пакет или listing"
              value={filters.query}
              onChange={(event) => filter({ ...filters, query: event.target.value })}
            />
            <div className="inventory-facets" role="group" aria-label="Какие Store Listings показать">
              {FACETS.map((facet) => (
                <button
                  key={facet}
                  type="button"
                  aria-pressed={filters.facet === facet}
                  // A facet with nothing in it has nothing to show; the pressed one stays so it can be left.
                  disabled={facetTotals[facet] === 0 && filters.facet !== facet}
                  onClick={() => filter({ ...filters, facet: filters.facet === facet ? 'all' : facet })}
                >
                  {FACET_LABEL[facet]}{' '}
                  <span className="facet-count">{facetTotals[facet]}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="inventory-legend">
            <p role="status">
              {listed(shown) === everyListing
                ? `App: ${groups.length} · Store Listings: ${everyListing}`
                : `App: ${shown.length} из ${groups.length} · Store Listings: ${listed(shown)} из ${everyListing}`}
            </p>
            <ul className="meter-legend" aria-label="Как читать полоску покрытия">
              <li>
                <span className="swatch meter-covered" aria-hidden="true" />
                покрыто
              </li>
              <li>
                <span className="swatch meter-gap" aria-hidden="true" />
                Translation Gap: Play переводит машинно или показывает без перевода
              </li>
              <li>
                <span className="swatch meter-unread" aria-hidden="true" />
                не прочитано, в зазор не считается
              </li>
            </ul>
            {shown.length === 0 ? null : (
              <button type="button" className="ghost" onClick={() => setFolded(allFolded ? new Set() : new Set(shown.map((group) => group.entry.app.id)))}>
                {allFolded ? 'Развернуть все' : 'Свернуть все'}
              </button>
            )}
          </div>
          {shown.length === 0 ? (
            <EmptyState
              title="Ничего не подходит."
              note="Ни один Store Listing не проходит поиск и фильтр вместе."
              action={
                <button type="button" className="secondary" onClick={() => filter(NO_FILTERS)}>
                  Сбросить фильтры
                </button>
              }
            />
          ) : null}
          {shown.map((group) => (
            <AppBlock
              key={group.entry.app.id}
              group={group}
              folded={folded.has(group.entry.app.id)}
              onFold={() => fold(group.entry.app.id)}
              onChanged={reload}
              onArchived={archived}
            />
          ))}
        </>
      )}
      <ArchivedApps apps={loaded.value.archived} onRestored={restored} />
      {notice === null ? null : (
        <div className="archive-notice" role="status">
          <p>{notice.archived ? `${notice.app.displayName} в архиве.` : `${notice.app.displayName} снова в Inventory.`}</p>
          {notice.failure === null ? null : <p className="failure">{notice.failure}</p>}
          {notice.archived ? (
            <button type="button" className="secondary" onClick={() => restore(notice.app)}>
              Вернуть из архива
            </button>
          ) : null}
          <button type="button" className="ghost" onClick={() => setNotice(null)}>
            Скрыть
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * What one Tracker Import did: what it added, by name; what was already here and was
 * left alone; and what it skipped, each with the reason.
 */
function TrackerImportSummary({ report }: { report: TrackerImportReport }): ReactNode {
  const { apps, storeListings, skipped } = report;
  const added = apps.added.length + storeListings.added.length;
  const existing = apps.existing + storeListings.existing;

  return (
    <section className="tracker-import" role="status" aria-label="Tracker Import">
      <p className="eyebrow">Tracker Import</p>
      {added + existing + skipped.length === 0 ? (
        <p>В Tracker нет приложений.</p>
      ) : (
        <>
          {added === 0 ? <p>Нового нет.</p> : null}
          {apps.added.length === 0 ? null : (
            <>
              <p>{`Добавлено App: ${apps.added.length}`}</p>
              <ul aria-label="Добавленные App">
                {apps.added.map((app) => (
                  <li key={app.id}>{`${app.displayName} · ${app.packageName}`}</li>
                ))}
              </ul>
            </>
          )}
          {storeListings.added.length === 0 ? null : (
            <>
              <p>{`Добавлено Custom Store Listings: ${storeListings.added.length}`}</p>
              <ul aria-label="Добавленные Custom Store Listings">
                {storeListings.added.map((listing) => (
                  <li key={listing.id}>{`${listing.appDisplayName} · listing=${listing.listingParam}`}</li>
                ))}
              </ul>
            </>
          )}
          {existing === 0 ? null : (
            <p>{`Уже было и не менялось: App — ${apps.existing}, Custom Store Listings — ${storeListings.existing}.`}</p>
          )}
          {skipped.length === 0 ? null : (
            <>
              <p>{`Пропущено: ${skipped.length}`}</p>
              <ul aria-label="Пропущено">
                {/* By position: two apps of the same name skipped for the same reason are two lines. */}
                {skipped.map((entry, index) => (
                  <li key={index}>{`${entry.subject}: ${entry.reason}`}</li>
                ))}
              </ul>
            </>
          )}
          {added === 0 ? null : (
            <p className="note">Inventory Scan сам не запускается: нажмите Scan у нужного App или Scan all.</p>
          )}
        </>
      )}
    </section>
  );
}

function AppBlock({
  group,
  folded,
  onFold,
  onChanged,
  onArchived,
}: {
  group: AppGroup;
  folded: boolean;
  onFold: () => void;
  onChanged: () => void;
  onArchived: (app: AppResource) => void;
}): ReactNode {
  const { entry, lines } = group;
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const reading = appIsReading(entry);
  const waiting = entry.scanPending && !reading;
  const name = entry.app.displayName;

  const scan = (): void => {
    setBusy(true);
    setFailure(null);
    void api
      .startScan(entry.app.id)
      .then(onChanged)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось запустить сканирование.'))
      .finally(() => setBusy(false));
  };

  const [archiving, setArchiving] = useState(false);
  const archive = (): void => {
    setArchiving(true);
    setFailure(null);
    void api
      .archiveApp(entry.app.id)
      .then(onArchived)
      .catch((caught: unknown) => {
        setFailure(caught instanceof ApiError ? caught.message : 'Не удалось убрать App в архив.');
        setArchiving(false);
      });
  };

  return (
    <article className={folded ? 'inventory-app folded' : 'inventory-app'} aria-label={name}>
      <header className="inventory-app-head">
        <button type="button" className="app-fold" aria-expanded={!folded} aria-label={`Store Listings · ${name}`} onClick={onFold}>
          <span className="chevron" aria-hidden="true" />
        </button>
        <div className="app-summary">
          <div className="app-identity">
            <h2>
              <Link to={`/apps/${entry.app.id}`}>{name}</Link>
            </h2>
            <p className="inventory-id">{entry.app.packageName}</p>
          </div>
          <p className="app-tally">
            <span>
              {lines.length === entry.listings.length
                ? `Store Listings: ${lines.length}`
                : `Store Listings: ${lines.length} из ${entry.listings.length}`}
            </span>
            {group.withGaps === 0 ? null : <span className="tally-gap">{`с Translation Gaps: ${group.withGaps}`}</span>}
          </p>
        </div>
        <div className="inventory-scan">
          <span className={entry.latestScan?.notOnPlay === true ? 'note failure' : 'note'}>{scanLine(entry.latestScan)}</span>
          {waiting ? <span className="note">Scan в очереди: начнётся, когда закончится предыдущий.</span> : null}
          {entry.latestScan?.stopReason === null || entry.latestScan === null ? null : (
            <span className="note">{entry.latestScan.stopReason}</span>
          )}
          {/* The line it stands on names the App; what a reader hears names it too. */}
          <button
            type="button"
            className="secondary"
            disabled={busy || entry.scanPending}
            aria-label={reading || waiting ? undefined : `Scan ${name}`}
            onClick={scan}
          >
            {reading ? 'Сканирую…' : waiting ? 'В очереди' : 'Scan'}
          </button>
        </div>
      </header>
      {failure === null ? null : <Failure message={failure} />}
      {folded ? null : (
        <>
          <ul className="inventory-listings" aria-label={`Store Listings · ${name}`}>
            {lines.map((line) => (
              <ListingRow
                key={line.listing.storeListing.id}
                line={line}
                app={entry.app}
                appReading={reading}
                onChanged={onChanged}
              />
            ))}
          </ul>
          {/* What is added to the App and how the App itself leaves the ledger: the quiet end of it. */}
          <div className="inventory-app-foot">
            <RegisterListings appId={entry.app.id} onChanged={onChanged} />
            <button type="button" className="ghost" disabled={archiving} aria-label={`Убрать App в архив: ${name}`} onClick={archive}>
              Убрать App в архив
            </button>
          </div>
        </>
      )}
    </article>
  );
}

/**
 * One Store Listing: a line that says where it stands, and under it, once pressed, what
 * can be done with it and what it shows where.
 */
function ListingRow({
  line,
  app,
  appReading,
  onChanged,
}: {
  line: ListingLine;
  app: AppResource;
  appReading: boolean;
  onChanged: () => void;
}): ReactNode {
  const { listing, summary, standing } = line;
  const groups = useMemo(() => gapsByLanguage(summary.gaps), [summary]);
  const coverage = useMemo(() => coverageOf(summary), [summary]);
  const machineTranslated = summary.gaps.filter((cell) => cell.machineTranslated).length;
  const [expanded, setExpanded] = useState(false);
  const [allGaps, setAllGaps] = useState(false);
  const [open, setOpen] = useState(false);
  const [chosenCountries, setCountries] = useState<'all' | 'gap' | 'unread'>('all');
  const [showing, setShowing] = useState<Showing | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const { storeListing } = listing;
  const main = storeListing.kind === 'main';
  const label = main ? 'Main Store Listing' : storeListing.name;
  const detailId = `listing-detail-${storeListing.id}`;
  const [changingLanguage, setChangingLanguage] = useState(false);
  const unreadCountries = summary.countries.filter((country) => country.state === 'unread').length;
  // A scan can take away the last country a narrowed table was showing.
  const countries =
    (chosenCountries === 'gap' && coverage.gap === 0) || (chosenCountries === 'unread' && unreadCountries === 0) ? 'all' : chosenCountries;

  // Pressing what is already open closes it, as the Close button does.
  const show = (cell: InventoryCell, from: Showing['from']): void => {
    const closing = showing !== null && showing.from === from && sameCell(showing.cell, cell);
    setShowing(closing ? null : { cell, from });
    rememberCellInAddress(
      closing ? null : { storeListingId: storeListing.id, market: cell.market, locale: cell.locale },
    );
  };
  const close = (): void => {
    setShowing(null);
    rememberCellInAddress(null);
  };

  /**
   * The cell the address names is open again: coming back from an Experiment has to
   * land on the same reading, not on the closed list.
   *
   * Once, on purpose: after that the cell in the address is the operator's own, and a
   * later reading must not reopen a cell they have since closed or replaced.
   */
  const [restored, setRestored] = useState(() => cellInAddress(window.location.search));
  useEffect(() => {
    if (restored === null || restored.storeListingId !== storeListing.id) return;
    const cell = listing.cells.find((one) => one.market === restored.market && one.locale === restored.locale);
    if (cell === undefined) return;
    setRestored(null);
    setExpanded(true);
    setOpen(true);
    setShowing({ cell, from: 'countries' });
  }, [restored, storeListing.id, listing.cells]);

  /**
   * The comparison of two readings, opened from the cell's label or from the reading
   * itself: one comparison, one place it is shown.
   */
  const [comparison, setComparison] = useState<ComparisonTarget | null>(null);
  const compare = (target: ComparisonTarget): void => {
    setComparison(target);
    rememberComparisonInAddress(target);
  };
  const closeComparison = (): void => {
    setComparison(null);
    rememberComparisonInAddress(null);
  };
  const [restoredComparison, setRestoredComparison] = useState(() => comparisonInAddress(window.location.search));
  useEffect(() => {
    if (restoredComparison === null || restoredComparison.storeListingId !== storeListing.id) return;
    setRestoredComparison(null);
    setExpanded(true);
    setComparison(restoredComparison);
  }, [restoredComparison, storeListing.id]);
  // The cell the comparison is about: one Market Locale of this Store Listing.
  const comparedCell =
    comparison === null
      ? null
      : (listing.cells.find((one) => one.market === comparison.market && one.locale === comparison.locale) ?? null);
  const reading = listing.latestScan?.state === 'reading' || (listing.scanPending && appReading);
  const when = listingScanOf(listing, appReading);

  const scan = (): void => {
    setStarting(true);
    setFailure(null);
    void api
      .startListingScan(storeListing.id)
      .then(onChanged)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось запустить сканирование.'))
      .finally(() => setStarting(false));
  };

  const archive = (): void => {
    setFailure(null);
    void api
      .archiveStoreListing(storeListing.id)
      .then(onChanged)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось удалить этот listing.'));
  };

  return (
    <li className="inventory-listing">
      <button
        type="button"
        className="listing-line"
        aria-expanded={expanded}
        aria-controls={expanded ? detailId : undefined}
        onClick={() => setExpanded((shown) => !shown)}
      >
        <span className="chevron" aria-hidden="true" />
        <span className="listing-identity">
          <strong>{label}</strong>
          {storeListing.listingParam === null ? null : <span className="inventory-id">{`listing=${storeListing.listingParam}`}</span>}
        </span>
        <span className="listing-reading">
          {standing !== 'read' ? (
            <span className="listing-standing">{STANDING_LABEL[standing]}</span>
          ) : (
            <>
              {/* Countries, not Market Locales: a country is covered when every language it reads is. */}
              <span
                className="meter"
                role="img"
                aria-label={`${summary.covered} из ${summary.total} стран покрыто`}
                title={`Покрыто: ${coverage.covered} · Translation Gap: ${coverage.gap} · не прочитано: ${coverage.unread}`}
              >
                {coverage.covered === 0 ? null : <span className="meter-covered" style={{ flexGrow: coverage.covered }} />}
                {coverage.gap === 0 ? null : <span className="meter-gap" style={{ flexGrow: coverage.gap }} />}
                {coverage.unread === 0 ? null : <span className="meter-unread" style={{ flexGrow: coverage.unread }} />}
              </span>
              <span className="listing-covered" aria-hidden="true">{`${summary.covered}/${summary.total}`}</span>
              {summary.gaps.length > 0 ? (
                <>
                  <span className="badge state-unavailable">{`${summary.gaps.length} Translation Gaps`}</span>
                  <span className="listing-fact">{counted(groups.length, 'язык', 'языка', 'языков')}</span>
                </>
              ) : summary.unread === 0 ? (
                <span className="badge state-ready">Translation Gaps нет</span>
              ) : null}
              {summary.untranslatedGraphics.length === 0 ? null : <span className="badge">{`${summary.untranslatedGraphics.length} Untranslated Graphics`}</span>}
              {summary.unread === 0 ? null : <span className="badge">{`${summary.unread} не прочитано`}</span>}
              {summary.changed === 0 ? null : <span className="badge">{`${summary.changed} изменилось`}</span>}
              {listing.possiblyRetired ? <span className="badge state-unavailable">возможно удалён</span> : null}
            </>
          )}
        </span>
        {when === null ? null : <span className={listing.latestScan?.notOnPlay === true ? 'listing-when failure' : 'listing-when'}>{when}</span>}
      </button>

      {expanded ? (
        <div className="listing-detail" id={detailId}>
          <div className="listing-actions">
            {storeListing.url === null ? null : (
              <>
                <button type="button" className="secondary" disabled={starting || listing.scanPending} onClick={scan}>
                  {reading ? 'Сканирую…' : listing.scanPending ? 'В очереди' : `Scan ${label}`}
                </button>
                <a href={storeListing.url} target="_blank" rel="noreferrer">
                  Открыть в Google Play
                </a>
              </>
            )}
            {/* A scan that is running is told on the line above, as it goes. */}
            {listing.latestScan === null || listing.latestScan.state === 'reading' ? null : (
              <span className={listing.latestScan.notOnPlay ? 'note failure' : 'note'}>{scanLine(listing.latestScan)}</span>
            )}
            {main ? null : (
              <button type="button" className="ghost listing-archive" onClick={archive}>
                {`Удалить ${label} из Inventory`}
              </button>
            )}
          </div>
          {listing.latestScan === null || listing.latestScan.stopReason === null ? null : (
            <p className="note">{listing.latestScan.stopReason}</p>
          )}
          {failure === null ? null : <Failure message={failure} />}

          {standing === 'noUrl' ? (
            <p className="note">Custom Store Listing без URL нельзя открыть вне Play Console.</p>
          ) : changingLanguage ? (
            <ListingLanguage
              storeListingId={storeListing.id}
              label={label}
              current={storeListing.language ?? DEFAULT_CUSTOM_LISTING_LANGUAGE}
              onCancel={() => setChangingLanguage(false)}
              onSaved={() => {
                setChangingLanguage(false);
                onChanged();
              }}
            />
          ) : standing === 'unread' ? (
            <p className="note">Просканируйте, чтобы прочитать во всех странах.</p>
          ) : (
            <>
              {main && listing.defaultLocale === null && machineTranslated === 0 ? null : (
                <p className="inventory-summary">
                  {listing.defaultLocale === null ? null : <span className="note">{`Язык по умолчанию: ${languageName(listing.defaultLocale)}`}</span>}
                  {machineTranslated === 0 ? null : <span className="badge">{`${machineTranslated} машинно переведено Google Play`}</span>}
                  {main ? null : (
                    <button type="button" className="ghost" onClick={() => setChangingLanguage(true)}>
                      Сменить язык
                    </button>
                  )}
                </p>
              )}
              {listing.possiblyRetired ? (
                <p className="note failure">Везде показывает Main Store Listing: возможно удалён или выключен в Play Console.</p>
              ) : null}
              {summary.gaps.length === 0 ? null : (
                <ul className="gap-list" aria-label={`Translation Gaps · ${label}`}>
                  {(allGaps ? groups : groups.slice(0, GAP_LANGUAGES_SHOWN)).map((group) => (
                    <li key={group.locale}>
                      <button
                        type="button"
                        className="chip"
                        title={group.cells.map((cell) => countryName(cell.market)).join(', ')}
                        aria-expanded={showing?.from === 'gaps' && sameCell(showing.cell, group.cells[0])}
                        onClick={() => {
                          const first = group.cells[0];
                          if (first !== undefined) show(first, 'gaps');
                        }}
                      >
                        {`${languageName(group.locale)} · ${
                          group.cells.length === 1
                            ? countryName(group.cells[0]?.market ?? '')
                            : counted(group.cells.length, 'страна', 'страны', 'стран')
                        }${group.machineTranslated ? '' : ' · без перевода'}`}
                      </button>
                    </li>
                  ))}
                  {groups.length <= GAP_LANGUAGES_SHOWN ? null : (
                    <li>
                      <button type="button" className="ghost" onClick={() => setAllGaps((shown) => !shown)}>
                        {allGaps ? 'Меньше языков' : `ещё ${counted(groups.length - GAP_LANGUAGES_SHOWN, 'язык', 'языка', 'языков')}`}
                      </button>
                    </li>
                  )}
                </ul>
              )}
              {showing?.from === 'gaps' ? (
                <SnapshotView cell={showing.cell} listing={listing} app={app} onClose={close} onCompare={compare} />
              ) : null}
              <div className="row-actions">
                <button type="button" className="ghost" aria-expanded={open} onClick={() => setOpen((shown) => !shown)}>
                  {open ? 'Скрыть страны' : `Показать все ${summary.countries.length} стран`}
                </button>
                {/* 225 countries are a table to search by eye; the ones to act on are a few dozen. */}
                {!open || coverage.gap + unreadCountries === 0 ? null : (
                  <div className="country-choice" role="group" aria-label={`Какие страны показать · ${label}`}>
                    <button type="button" aria-pressed={countries === 'all'} onClick={() => setCountries('all')}>
                      {`Все ${summary.countries.length}`}
                    </button>
                    {coverage.gap === 0 ? null : (
                      <button type="button" aria-pressed={countries === 'gap'} onClick={() => setCountries('gap')}>
                        {`С Translation Gap ${coverage.gap}`}
                      </button>
                    )}
                    {unreadCountries === 0 ? null : (
                      <button type="button" aria-pressed={countries === 'unread'} onClick={() => setCountries('unread')}>
                        {`Не прочитано ${unreadCountries}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {open ? (
                <CountryTable
                  countries={countries === 'all' ? summary.countries : summary.countries.filter((country) => country.state === countries)}
                  listing={listing}
                  app={app}
                  showing={showing?.from === 'countries' ? showing.cell : null}
                  onShow={(cell) => show(cell, 'countries')}
                  onClose={close}
                  onCompare={compare}
                />
              ) : null}
              {comparedCell === null ? null : (
                // One comparison per pair of readings: another pair starts from nothing, so a
                // reading of the last pair can never answer for one of this pair that failed.
                <Comparison
                  key={`${comparedCell.snapshotId ?? ''}|${comparedCell.previousSnapshotId ?? ''}`}
                  app={app}
                  listing={listing}
                  cell={comparedCell}
                  onClose={closeComparison}
                />
              )}
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

/**
 * What a cell says about its graphics: its own, or Untranslated Graphics. By the one rule
 * the facet and the export ask too; the default language's graphics under the default
 * language's text are not marked.
 */
function graphicsFlag(graphics: CellGraphics): ReactNode {
  return graphics === 'untranslated' || graphics === 'own' ? <span className="cell-flag">{GRAPHICS_LABEL[graphics]}</span> : null;
}

function CountryTable({
  countries,
  listing,
  app,
  showing,
  onShow,
  onClose,
  onCompare,
}: {
  countries: readonly CountryLine[];
  /** The Store Listing and the App the cells belong to: the way out of the Inventory. */
  listing: InventoryListing;
  app: AppResource;
  /** The reading open under its country's row. */
  showing: InventoryCell | null;
  onShow: (cell: InventoryCell) => void;
  onClose: () => void;
  /** The comparison of the two readings of one cell. */
  onCompare: (target: ComparisonTarget) => void;
}): ReactNode {
  return (
    <div className="country-table-scroll">
      <table className="country-table">
        <thead>
          <tr>
            <th scope="col">Страна</th>
            <th scope="col">Языки, которые читает</th>
          </tr>
        </thead>
        <tbody>
          {countries.map((country) => (
            <Fragment key={country.market}>
              <tr className={`country-${country.state}`}>
                <th scope="row">{`${countryName(country.market)} (${country.market})`}</th>
                <td>
                  {country.cells.map((cell) => (
                    <Fragment key={cell.locale}>
                      <button
                        type="button"
                        className={`cell cell-${cell.state}`}
                        disabled={cell.snapshotId === null}
                        aria-expanded={sameCell(showing, cell)}
                        aria-label={
                          cell.snapshotId === null
                            ? `${languageName(cell.locale)} · ${CELL_LABEL[cell.state]} · переход в Experiment недоступен: это чтение ещё не прочитано`
                            : undefined
                        }
                        onClick={() => onShow(cell)}
                      >
                        <span>{languageName(cell.locale)}</span>
                        <span className="cell-state">{CELL_LABEL[cell.state]}</span>
                        {cell.machineTranslated ? <span className="cell-flag">машинный перевод</span> : null}
                        {graphicsFlag(cellGraphicsOf(cell, listing.defaultLocale))}
                      </button>
                      {/* The label of the change is the way into the comparison of the two readings. */}
                      {cell.changed ? (
                        <button
                          type="button"
                          className="cell-flag ghost"
                          aria-label={`изменено · ${languageName(cell.locale)}`}
                          onClick={() =>
                            onCompare({ storeListingId: listing.storeListing.id, market: cell.market, locale: cell.locale })
                          }
                        >
                          изменилось
                        </button>
                      ) : null}
                    </Fragment>
                  ))}
                </td>
              </tr>
              {showing?.market === country.market ? (
                <tr className="country-snapshot">
                  <td colSpan={2}>
                    <SnapshotView cell={showing} listing={listing} app={app} onClose={onClose} onCompare={onCompare} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
      {countries.some((country) => country.cells.some((cell) => cell.snapshotId === null)) ? (
        <p className="note">Непрочитанные клетки не берутся в Experiment: сначала прочитайте их — Scan читает Store Listing целиком.</p>
      ) : null}
    </div>
  );
}

/** What one Market Locale shows, exactly as it was read. */
function SnapshotView({
  cell,
  listing,
  app,
  onClose,
  onCompare,
}: {
  cell: InventoryCell;
  listing: InventoryListing;
  app: AppResource;
  onClose: () => void;
  /** The comparison of this reading and the one before it. */
  onCompare: (target: ComparisonTarget) => void;
}): ReactNode {
  const loaded = useAsync<ReadingById | null>(
    async () => (cell.snapshotId === null ? null : readById(cell.snapshotId)),
    [cell.snapshotId],
  );
  // `useAsync` keeps the last value while the next one loads: another country's words -
  // or its failure - must never sit under this country's name.
  const current = loaded.value?.id === cell.snapshotId ? loaded.value : null;
  const snapshot = current?.snapshot ?? null;
  // The icon is the same everywhere and only shown when it could not be kept; an image
  // that was not retrieved keeps its place and says so, never silently drops out.
  const images = (snapshot?.assets ?? []).filter(
    (asset) => asset.role !== 'previewVideo' && (asset.role !== 'appIcon' || asset.artifactId === null),
  );

  return (
    <section className="snapshot-view" aria-label={`${countryName(cell.market)} · ${languageName(cell.locale)}`}>
      <div className="inventory-listing-head">
        <p className="eyebrow">{`${countryName(cell.market)} · ${languageName(cell.locale)} · ${CELL_LABEL[cell.state]}`}</p>
        <button type="button" className="ghost" onClick={onClose}>
          Закрыть
        </button>
      </div>
      {current !== null && current.error !== null ? (
        <Failure message={current.error} />
      ) : snapshot === null ? (
        <p className="note" aria-busy="true">
          Читаю…
        </p>
      ) : (
        <>
          <h3>{snapshot.title ?? 'Заголовок не прочитан'}</h3>
          {/* The cell holds when the storefront was read; a correction was written later. */}
          <p className="note">{`${snapshot.label} · ${readingWhen(cell.capturedAt ?? snapshot.capturedAt, snapshot.reviewedAt)}`}</p>
          {snapshot.machineTranslated ? (
            <p className="note failure">Google Play перевёл этот текст сам: у listing нет перевода на этот язык.</p>
          ) : null}
          {snapshot.warnings.map((warning) => (
            <p key={warning} className="note failure">
              {warning}
            </p>
          ))}
          {snapshot.shortDescription === null ? null : <p>{snapshot.shortDescription}</p>}
          {snapshot.fullDescription === null ? null : (
            <details>
              <summary>Полное описание</summary>
              <p className="snapshot-text">{snapshot.fullDescription}</p>
            </details>
          )}
          <ul className="snapshot-images" aria-label="Графика, показанная здесь">
            {images.map((asset) =>
              asset.artifactId === null ? (
                <li key={asset.id} className="snapshot-image-missing">
                  <strong>{`${ASSET_LABEL[asset.role]} не получено`}</strong>
                  <span className="note failure">{asset.warning ?? 'Не удалось получить это изображение из Studio.'}</span>
                  {asset.sourceUrl === null ? null : (
                    <a href={asset.sourceUrl} target="_blank" rel="noreferrer">
                      Открыть изображение в Google Play
                    </a>
                  )}
                </li>
              ) : (
                <li key={asset.id}>
                  <img className="frame" src={`${ARTIFACTS_PATH}/${asset.artifactId}`} alt={ASSET_LABEL[asset.role]} loading="lazy" />
                </li>
              ),
            )}
          </ul>
          <div className="row-actions">
            {snapshot.sourceUrl === null ? null : (
              <a href={snapshot.sourceUrl} target="_blank" rel="noreferrer">
                Эта страница в Google Play
              </a>
            )}
            <Link to={`/apps/${snapshot.appId}?review=${snapshot.id}`}>Исправить это чтение</Link>
            {/* The way into the comparison: the same one the cell's label opens. */}
            <button
              type="button"
              className="ghost"
              onClick={() => onCompare({ storeListingId: listing.storeListing.id, market: cell.market, locale: cell.locale })}
            >
              Сравнить с прошлым чтением
            </button>
          </div>
        </>
      )}
      {/* The way out of the Inventory: the cell the operator is looking at is the one to start from. */}
      <StartExperiment app={app} listing={listing} cell={cell} cellReading={current} onRereadCell={loaded.reload} />
    </section>
  );
}

/** Every Market Locale language, by the name a reader knows it by. */
const LANGUAGE_CHOICES = [...MARKET_LANGUAGES]
  .map((language) => ({ language, name: languageName(language) }))
  .sort((left, right) => left.name.localeCompare(right.name));

function LanguageSelect({ id, value, onChange }: { id: string; value: string; onChange: (language: string) => void }): ReactNode {
  return (
    <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
      {LANGUAGE_CHOICES.map((choice) => (
        <option key={choice.language} value={choice.language}>
          {choice.name}
        </option>
      ))}
    </select>
  );
}

/**
 * The language a Custom Store Listing is written in. Google Play shows it untranslated
 * to every language, so only the operator can say when it is not English.
 */
function ListingLanguage({
  storeListingId,
  label,
  current,
  onCancel,
  onSaved,
}: {
  storeListingId: string;
  label: string;
  current: string;
  onCancel: () => void;
  onSaved: () => void;
}): ReactNode {
  const [language, setLanguage] = useState(current);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const id = `listing-language-${storeListingId}`;

  const save = (event: FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setFailure(null);
    void api
      .setStoreListingLanguage(storeListingId, { language })
      .then(onSaved)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось сохранить язык.'))
      .finally(() => setBusy(false));
  };

  return (
    <form className="listing-language" onSubmit={save} aria-label={`Язык · ${label}`}>
      <label htmlFor={id}>Написан на</label>
      <div className="listing-language-row">
        <LanguageSelect id={id} value={language} onChange={setLanguage} />
        <button type="submit" className="secondary" disabled={busy}>
          {busy ? 'Сохраняю…' : 'Сохранить язык'}
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Отмена
        </button>
      </div>
      {failure === null ? null : <Failure message={failure} />}
    </form>
  );
}

/**
 * Custom Store Listings are named by the operator: Play offers no way to list them from
 * outside Play Console.
 */
function RegisterListings({ appId, onChanged }: { appId: string; onChanged: () => void }): ReactNode {
  const [addresses, setAddresses] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState(DEFAULT_CUSTOM_LISTING_LANGUAGE);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ readonly failure: boolean; readonly text: string } | null>(null);

  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      const lines = addresses.split(/\s+/).filter((line) => line !== '');
      setBusy(true);
      setMessage(null);
      void api
        .registerStoreListings(appId, {
          ...(lines.length === 0 ? {} : { addresses: lines }),
          ...(name.trim() === '' ? {} : { name: name.trim() }),
          language,
        })
        .then((result) => {
          setAddresses(result.refused.map((one) => one.entered).join('\n'));
          setName('');
          const refused = result.refused.map((one) => `${one.entered}: ${one.problem}`).join(' ');
          setMessage({
            failure: result.refused.length > 0,
            text: `${result.listings.length} добавлено.${refused === '' ? '' : ` Не добавлено: ${refused}`}`,
          });
          onChanged();
        })
        .catch((caught: unknown) => setMessage({ failure: true, text: caught instanceof ApiError ? caught.message : 'Не удалось добавить эти listings.' }))
        .finally(() => setBusy(false));
    },
    [addresses, appId, language, name, onChanged],
  );

  return (
    <details className="register-listings">
      <summary>Добавить Custom Store Listings</summary>
      <form onSubmit={submit}>
        <label htmlFor={`listing-addresses-${appId}`}>Адреса с &amp;listing=…, по одному в строке</label>
        <textarea
          id={`listing-addresses-${appId}`}
          rows={3}
          value={addresses}
          onChange={(event) => setAddresses(event.target.value)}
          placeholder="https://play.google.com/store/apps/details?id=…&listing=spring.promo"
        />
        <label htmlFor={`listing-name-${appId}`}>Или имя без URL</label>
        <input id={`listing-name-${appId}`} value={name} onChange={(event) => setName(event.target.value)} placeholder="Brazil, pre-registration" />
        <label htmlFor={`listing-language-new-${appId}`}>Написан на</label>
        <LanguageSelect id={`listing-language-new-${appId}`} value={language} onChange={setLanguage} />
        <button type="submit" className="secondary" disabled={busy || (addresses.trim() === '' && name.trim() === '')}>
          {busy ? 'Добавляю…' : 'Добавить в Inventory'}
        </button>
        {message === null ? null : (
          <p className={message.failure ? 'failure' : 'note'} role={message.failure ? 'alert' : 'status'}>
            {message.text}
          </p>
        )}
        <p className="note">Их прочитают при следующем scan.</p>
      </form>
    </details>
  );
}

export const surface: Surface = {
  id: 'inventory',
  path: '/inventory',
  title: 'Inventory',
  render: () => <Inventory />,
};
